import { ResourcesSync } from './ResourcesSync.js';
import { TeamMail } from './TeamMail.js';
import { TeamOrganizer } from './TeamOrganizer.js';
import { exactLocalToIso, browserZone } from './time.js';
import { t, localeTag } from './i18n.js';
import { GuestPicker, guestAddresses } from './GuestPicker.js';
import { TeamOccurrence } from './TeamOccurrence.js';
import { TeamAvailability } from './TeamAvailability.js';
import {
  teamDays,
  teamRange,
  moveTeamDate,
  localDate,
  overlapsDay,
  type TeamView,
} from '../shared/team-calendar.js';
import { useEffect, useState, useRef } from 'react';
import { api, send } from './api.js';
import { Notice } from './components.js';

type Person = {
  id: string;
  name: string;
  email: string;
  source: string;
  phone?: string;
  company?: string;
};
export function Collaboration({ role }: { role: string }) {
  const [people, setPeople] = useState<Person[]>([]),
    [calendars, setCalendars] = useState<any[]>([]),
    [events, setEvents] = useState<any[]>([]),
    [members, setMembers] = useState<any[]>([]),
    [resources, setResources] = useState<any[]>([]),
    [resourceId, setResourceId] = useState(''),
    [selected, setSelected] = useState(''),
    [enabled, setEnabled] = useState(false),
    [self, setSelf] = useState(''),
    [anchor, setAnchor] = useState(localDate(new Date())),
    [view, setView] = useState<TeamView>('month'),
    [focused, setFocused] = useState<any>(),
    [loadingEvents, setLoadingEvents] = useState(false),
    [guests, setGuests] = useState(''),
    [guestQuery, setGuestQuery] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [query, setQuery] = useState(''),
    [contactId, setContactId] = useState(''),
    [recurrence, setRecurrence] = useState({
      frequency: 'once',
      count: 1,
      timezone: browserZone(),
      exceptions: [] as any[],
    });
  const [contact, setContact] = useState({ name: '', email: '', phone: '', company: '' }),
    [calendarName, setCalendarName] = useState(''),
    [member, setMember] = useState(''),
    [right, setRight] = useState('read');
  const [draft, setDraft] = useState({
    id: '',
    title: '',
    description: '',
    start: '',
    end: '',
    revision: 1,
  });
  const load = async () => {
    const caps = await api('/collaboration/capabilities');
    setEnabled(caps.enabled);
    setSelf(caps.userId);
    if (caps.enabled) {
      setPeople(await api('/directory'));
      setCalendars(await api('/team-calendars'));
      setResources(await api('/resources'));
    }
  };
  useEffect(() => {
    void load().catch((e) => setError(e.message));
  }, []);
  const eventKey = useRef(crypto.randomUUID());
  const eventTitle = useRef<HTMLInputElement>(null);
  const focusEvent = () =>
    requestAnimationFrame(() => {
      eventTitle.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      eventTitle.current?.focus();
    });
  const requestVersion = useRef(0);
  const days = teamDays(anchor, view);
  const range = teamRange(days);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const current = calendars.find((c) => c.id === selected);
  const loadEvents = async (id: string) => {
    const version = ++requestVersion.current;
    const { from, to } = range;
    setLoadingEvents(true);
    try {
      const rows = await api(
        `/team-calendars/${id}/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      );
      const shares =
        current?.right_name === 'owner' ? await api(`/team-calendars/${id}/members`) : [];
      if (selectedRef.current === id && version === requestVersion.current) {
        setEvents(rows);
        setFocused((old: any) =>
          old
            ? rows.find((e: any) => e.id === old.id && e.occurrenceStart === old.occurrenceStart)
            : undefined,
        );
        setMembers(shares);
      }
    } catch (e) {
      if (version === requestVersion.current && selectedRef.current === id) throw e;
    } finally {
      if (version === requestVersion.current) setLoadingEvents(false);
    }
  };
  useEffect(() => {
    ++requestVersion.current;
    setEvents([]);
    setFocused(undefined);
    setMembers([]);
    if (selected) void loadEvents(selected).catch((e) => setError(e.message));
    const timer = setInterval(() => {
      if (selected) void loadEvents(selected).catch((e) => setError(e.message));
    }, 60000);
    return () => {
      clearInterval(timer);
      ++requestVersion.current;
    };
  }, [selected, calendars, range.from, range.to]);
  useEffect(() => {
    setDraft({ id: '', title: '', description: '', start: '', end: '', revision: 1 });
    setRecurrence({ frequency: 'once', count: 1, timezone: browserZone(), exceptions: [] });
    setGuests('');
    setGuestQuery('');
    setResourceId('');
  }, [selected]);
  const run = async (fn: () => Promise<any>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const local = (v: string) => {
    const d = new Date(v);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  return (
    <section className="pro-workspace">
      <header>
        <h1>{t('Espace d’équipe')}</h1>
        <p>{t('Annuaire commun et calendriers partagés de votre organisation.')}</p>
      </header>
      {error && <Notice type="error">{error}</Notice>}
      {!enabled ? (
        <Notice>
          <p>
            {t(
              'Votre organisation n’a pas encore activé les fonctions d’équipe. Vous pourrez y retrouver l’annuaire commun et les calendriers partagés.',
            )}
          </p>
          <p>
            {t('Demandez l’activation à votre administrateur ou contactez le support Vename.')}{' '}
            <a href="mailto:support@vename.com">{t('Écrire au support')}</a>
          </p>
        </Notice>
      ) : (
        <>
          <TeamMail role={role} />
          <section className="pro-card">
            <h2>{t('Annuaire partagé')}</h2>
            <label>
              {t('Rechercher un contact')}
              <input value={query} onChange={(e) => setQuery(e.target.value)} />
            </label>
            <div className="pro-people">
              {people
                .filter((p) =>
                  `${p.name} ${p.email} ${p.company ?? ''}`
                    .toLowerCase()
                    .includes(query.toLowerCase()),
                )
                .map((p) => (
                  <article key={`${p.source}-${p.id}`}>
                    <strong>{p.name}</strong>
                    <a href={`mailto:${p.email}`}>{p.email}</a>
                    <span>{p.source === 'member' ? 'Membre de l’organisation' : p.company}</span>
                    {p.phone && <span>{p.phone}</span>}
                    {p.source === 'contact' && ['owner', 'admin'].includes(role) && (
                      <button
                        disabled={busy}
                        onClick={() => {
                          setContactId(p.id);
                          setContact({
                            name: p.name,
                            email: p.email,
                            phone: p.phone ?? '',
                            company: p.company ?? '',
                          });
                        }}
                      >
                        {t('Modifier le contact')}
                      </button>
                    )}
                    {p.source === 'contact' && ['owner', 'admin'].includes(role) && (
                      <button
                        disabled={busy}
                        onClick={() =>
                          void run(async () => {
                            await send(`/directory/${p.id}`, {}, 'DELETE');
                            await load();
                          })
                        }
                      >
                        {t('Supprimer le contact')}
                      </button>
                    )}
                  </article>
                ))}
            </div>
            {['owner', 'admin'].includes(role) && (
              <form
                className="pro-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void run(async () => {
                    await send(
                      `/directory${contactId ? '/' + contactId : ''}`,
                      contact,
                      contactId ? 'PUT' : 'POST',
                    );
                    setContactId('');
                    setContact({ name: '', email: '', phone: '', company: '' });
                    await load();
                  });
                }}
              >
                <h3>{t(contactId ? 'Modifier le contact' : 'Ajouter un contact commun')}</h3>
                {(['name', 'email', 'phone', 'company'] as const).map((key, i) => (
                  <label key={key}>
                    {t(['Nom', 'Adresse email', 'Téléphone', 'Entreprise'][i])}
                    <input
                      type={key === 'email' ? 'email' : 'text'}
                      required={i < 2}
                      value={contact[key]}
                      onChange={(e) => setContact({ ...contact, [key]: e.target.value })}
                    />
                  </label>
                ))}
                <button disabled={busy}>
                  {t(contactId ? 'Enregistrer' : 'Ajouter à l’annuaire')}
                </button>
              </form>
            )}
          </section>
          <section className="pro-card">
            <h2>{t('Calendriers d’équipe')}</h2>
            <form
              className="pro-form"
              onSubmit={(e) => {
                e.preventDefault();
                void run(async () => {
                  const c = await send('/team-calendars', { name: calendarName });
                  setCalendarName('');
                  await load();
                  setSelected(c.id);
                });
              }}
            >
              <label>
                {t('Nom du nouveau calendrier')}
                <input
                  required
                  maxLength={150}
                  value={calendarName}
                  onChange={(e) => setCalendarName(e.target.value)}
                  placeholder="Équipe commerciale"
                />
              </label>
              <button disabled={busy}>{t('Créer le calendrier')}</button>
            </form>
            <label>
              {t('Calendrier à consulter')}
              <select value={selected} onChange={(e) => setSelected(e.target.value)}>
                <option value="">{t('Choisir un calendrier')}</option>
                {calendars.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} (
                    {c.right_name === 'read'
                      ? 'lecture'
                      : c.right_name === 'owner'
                        ? 'propriétaire'
                        : 'modification'}
                    )
                  </option>
                ))}
              </select>
            </label>
            {selected && (
              <>
                <div className="team-calendar-toolbar">
                  <div>
                    <button
                      type="button"
                      aria-label={t('Période précédente')}
                      onClick={() => setAnchor(moveTeamDate(anchor, view, -1))}
                    >
                      ←
                    </button>
                    <button type="button" onClick={() => setAnchor(localDate(new Date()))}>
                      {t('Aujourd’hui')}
                    </button>
                    <button
                      type="button"
                      aria-label={t('Période suivante')}
                      onClick={() => setAnchor(moveTeamDate(anchor, view, 1))}
                    >
                      →
                    </button>
                  </div>
                  <h3>
                    {view === 'month'
                      ? new Date(anchor + 'T12:00:00').toLocaleDateString(localeTag(), {
                          month: 'long',
                          year: 'numeric',
                        })
                      : `${days[0].toLocaleDateString(localeTag())} – ${days.at(-1)!.toLocaleDateString(localeTag())}`}
                  </h3>
                  <label>
                    {t('Date à consulter')}
                    <input
                      type="date"
                      required
                      value={anchor}
                      onChange={(e) => {
                        if (e.target.value) setAnchor(e.target.value);
                      }}
                    />
                  </label>
                  <div role="group" aria-label={t('Vue du calendrier d’équipe')}>
                    <button
                      type="button"
                      aria-pressed={view === 'week'}
                      onClick={() => setView('week')}
                    >
                      {t('Semaine')}
                    </button>
                    <button
                      type="button"
                      aria-pressed={view === 'month'}
                      onClick={() => setView('month')}
                    >
                      {t('Mois')}
                    </button>
                  </div>
                </div>
                <p>{t('Heures affichées dans votre fuseau local.')}</p>
                {loadingEvents && <p role="status">{t('Chargement des événements…')}</p>}
                <div
                  className={`team-calendar-grid team-calendar-${view}`}
                  aria-label={t('Événements du calendrier d’équipe')}
                >
                  {days.map((day) => (
                    <section
                      className={`team-calendar-day ${localDate(day) === localDate(new Date()) ? 'is-today' : ''} ${view === 'month' && localDate(day).slice(0, 7) !== anchor.slice(0, 7) ? 'is-outside' : ''}`}
                      key={localDate(day)}
                    >
                      <button
                        type="button"
                        className="team-day-title"
                        disabled={current?.right_name === 'read'}
                        aria-label={t('Créer un événement le {date}', {
                          date: day.toLocaleDateString(localeTag()),
                        })}
                        onClick={() => {
                          setFocused(undefined);
                          setRecurrence({
                            frequency: 'once',
                            count: 1,
                            timezone: browserZone(),
                            exceptions: [],
                          });
                          setGuests('');
                          setGuestQuery('');
                          setResourceId('');
                          setDraft({
                            id: '',
                            title: '',
                            description: '',
                            start: localDate(day) + 'T10:00',
                            end: localDate(day) + 'T11:00',
                            revision: 1,
                          });
                          focusEvent();
                        }}
                      >
                        {day.toLocaleDateString(localeTag(), { weekday: 'short', day: 'numeric' })}
                      </button>
                      {events
                        .filter((e) => overlapsDay(e.starts_at, e.ends_at, day))
                        .map((e) => (
                          <button
                            type="button"
                            className="team-event-chip"
                            key={e.id + ':' + e.starts_at}
                            onClick={() => {
                              setFocused(e);
                            }}
                          >
                            <time>
                              {localDate(new Date(e.starts_at)) === localDate(day)
                                ? new Date(e.starts_at).toLocaleTimeString(localeTag(), {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : t('En cours')}
                            </time>{' '}
                            <span>{e.title}</span>
                          </button>
                        ))}
                    </section>
                  ))}
                </div>
                {!loadingEvents && !events.length && (
                  <p>{t('Aucun événement sur cette période.')}</p>
                )}
                {[focused].filter(Boolean).map((e) => (
                  <article className="pro-event" key={e.id}>
                    <strong>{e.title}</strong>
                    <p>
                      {new Date(e.starts_at).toLocaleString()} →{' '}
                      {new Date(e.ends_at).toLocaleString()}
                    </p>
                    <p>{e.description}</p>
                    {e.resourceId && (
                      <p>
                        {t('Ressource réservée')} :{' '}
                        {resources.find((r) => r.id === e.resourceId)?.name ??
                          t('Ressource indisponible')}
                      </p>
                    )}
                    <TeamOrganizer
                      calendarId={selected}
                      event={e}
                      onChange={() => loadEvents(selected)}
                    />
                    {current?.right_name !== 'read' &&
                      e.canOrganize &&
                      e.recurrence?.frequency !== 'once' &&
                      e.recurrence && (
                        <TeamOccurrence
                          calendarId={selected}
                          event={e}
                          onChange={() => loadEvents(selected)}
                        />
                      )}
                    {e.attendees?.length > 0 && (
                      <div>
                        <h4>{t('Réponses des invités')}</h4>
                        {e.attendees.map((a: any) => (
                          <p key={a.email}>
                            {a.email} :{' '}
                            {t(
                              (
                                {
                                  accepted: 'Acceptée',
                                  declined: 'Refusée',
                                  tentative: 'Provisoire',
                                  'needs-action': 'En attente',
                                } as Record<string, string>
                              )[a.response],
                            )}
                          </p>
                        ))}
                        <p>
                          {t('Invitations : {sent} transmise(s), {pending} en attente', {
                            sent: e.deliveries.filter(
                              (d: any) =>
                                d.revision ===
                                  Math.max(...e.deliveries.map((j: any) => j.revision)) &&
                                d.status === 'submitted',
                            ).length,
                            pending: e.deliveries.filter(
                              (d: any) =>
                                d.revision ===
                                  Math.max(...e.deliveries.map((j: any) => j.revision)) &&
                                d.status === 'pending',
                            ).length,
                          })}
                        </p>
                      </div>
                    )}
                    {current?.right_name !== 'read' && e.canOrganize && (
                      <>
                        <button
                          disabled={busy}
                          onClick={() => {
                            setGuests((e.attendees ?? []).map((a: any) => a.email).join(', '));
                            setRecurrence(
                              e.recurrence ?? {
                                frequency: 'once',
                                count: 1,
                                timezone: browserZone(),
                                exceptions: [],
                              },
                            );
                            setResourceId(e.resourceId ?? '');
                            setDraft({
                              id: e.id,
                              title: e.title,
                              description: e.description,
                              start: local(e.seriesStart ?? e.starts_at),
                              end: local(e.seriesEnd ?? e.ends_at),
                              revision: e.revision,
                            });
                            focusEvent();
                          }}
                        >
                          {t('Modifier')}
                        </button>
                        <button
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              await send(
                                `/team-calendars/${selected}/events/${e.id}`,
                                { revision: e.revision },
                                'DELETE',
                              );
                              setFocused(undefined);
                              await loadEvents(selected);
                            })
                          }
                        >
                          {t('Supprimer')}
                        </button>
                      </>
                    )}
                  </article>
                ))}
                {current?.right_name !== 'read' && (
                  <form
                    className="pro-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void run(async () => {
                        const start = exactLocalToIso(draft.start, browserZone()),
                          end = exactLocalToIso(draft.end, browserZone());
                        if (!start || !end)
                          throw new Error(
                            t(
                              'Cette heure n’existe pas dans ce fuseau, à cause du changement d’heure.',
                            ),
                          );
                        await send(
                          `/team-calendars/${selected}/events${draft.id ? '/' + draft.id : ''}`,
                          {
                            title: draft.title,
                            description: draft.description,
                            ...recurrence,
                            resourceId: resourceId || null,
                            attendees: guestAddresses(guests),
                            requestKey: eventKey.current,
                            start,
                            end,
                            ...(draft.id ? { revision: draft.revision } : {}),
                          },
                          draft.id ? 'PUT' : 'POST',
                        );
                        setRecurrence({
                          frequency: 'once',
                          count: 1,
                          timezone: browserZone(),
                          exceptions: [],
                        });
                        eventKey.current = crypto.randomUUID();
                        setFocused(undefined);
                        setGuests('');
                        setGuestQuery('');
                        setResourceId('');
                        setDraft({
                          id: '',
                          title: '',
                          description: '',
                          start: '',
                          end: '',
                          revision: 1,
                        });
                        await loadEvents(selected);
                      });
                    }}
                  >
                    <h3>{t(draft.id ? 'Modifier l’événement' : 'Nouvel événement')}</h3>
                    <label>
                      {t('Titre')}
                      <input
                        required
                        ref={eventTitle}
                        value={draft.title}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      />
                    </label>
                    <label>
                      {t('Début')}
                      <input
                        required
                        type="datetime-local"
                        value={draft.start}
                        onChange={(e) => setDraft({ ...draft, start: e.target.value })}
                      />
                    </label>
                    <label>
                      {t('Fin')}
                      <input
                        required
                        type="datetime-local"
                        value={draft.end}
                        onChange={(e) => setDraft({ ...draft, end: e.target.value })}
                      />
                    </label>
                    <label>
                      {t('Salle ou équipement à réserver')}
                      <select
                        value={resourceId}
                        onFocus={() =>
                          void api('/resources')
                            .then(setResources)
                            .catch((e) => setError(e.message))
                        }
                        onChange={(e) => setResourceId(e.target.value)}
                      >
                        <option value="">{t('Aucune ressource')}</option>
                        {resourceId && !resources.some((r) => r.id === resourceId) && (
                          <option value={resourceId}>{t('Ressource indisponible')}</option>
                        )}
                        {resources.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <p>
                      {t(
                        'La ressource suit les horaires, les exceptions et l’annulation de la réunion. Un conflit empêche l’enregistrement et l’envoi des invitations.',
                      )}
                    </p>
                    <label>
                      {t('Description')}
                      <textarea
                        value={draft.description}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                      />
                    </label>
                    <label>
                      {t('Répétition')}
                      <select
                        aria-label={t('Répétition')}
                        value={recurrence.frequency}
                        onChange={(e) =>
                          setRecurrence({
                            ...recurrence,
                            frequency: e.target.value,
                            exceptions: [],
                          })
                        }
                      >
                        <option value="once">{t('Une seule fois')}</option>
                        <option value="daily">{t('Chaque jour')}</option>
                        <option value="weekly">{t('Chaque semaine')}</option>
                        <option value="monthly">{t('Chaque mois')}</option>
                      </select>
                    </label>
                    {recurrence.frequency !== 'once' && (
                      <label>
                        {t('Nombre d’occurrences')}
                        <input
                          type="number"
                          aria-label={t('Nombre d’occurrences')}
                          required
                          min="1"
                          max="100"
                          value={recurrence.count}
                          onChange={(e) =>
                            setRecurrence({ ...recurrence, count: Number(e.target.value) })
                          }
                        />
                        <small>
                          {t(
                            '100 occurrences maximum. Les exceptions sont conservées lors de la modification de la série.',
                          )}
                        </small>
                      </label>
                    )}
                    <div className="team-guest-field">
                      <GuestPicker
                        value={guests}
                        query={guestQuery}
                        onChange={setGuests}
                        onQueryChange={setGuestQuery}
                      />
                      <p>{t('Les invités recevront une invitation depuis votre adresse email.')}</p>
                    </div>
                    <button disabled={busy}>
                      {t(guests ? 'Enregistrer et inviter' : 'Enregistrer')}
                    </button>
                  </form>
                )}
                {current?.right_name === 'owner' && (
                  <form
                    className="pro-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void run(async () => {
                        await send(
                          `/team-calendars/${selected}/members`,
                          { userId: member, right },
                          'PUT',
                        );
                        await loadEvents(selected);
                      });
                    }}
                  >
                    <h3>{t('Partager le calendrier')}</h3>
                    {members.map((m) => (
                      <p key={m.user_id}>
                        {m.name} : {m.right_name === 'read' ? 'lecture' : 'modification'}
                      </p>
                    ))}
                    <label>
                      {t('Membre')}
                      <select required value={member} onChange={(e) => setMember(e.target.value)}>
                        <option value="">{t('Choisir un membre')}</option>
                        {people
                          .filter((p) => p.source === 'member' && p.id !== current.owner_id)
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} ({p.email})
                            </option>
                          ))}
                      </select>
                    </label>
                    <label>
                      {t('Droit')}
                      <select value={right} onChange={(e) => setRight(e.target.value)}>
                        <option value="read">{t('Consulter')}</option>
                        <option value="write">{t('Consulter et modifier')}</option>
                        <option value="none">{t('Retirer l’accès')}</option>
                      </select>
                    </label>
                    <button disabled={busy}>{t('Appliquer le partage')}</button>
                  </form>
                )}
              </>
            )}
          </section>
          <ResourcesSync role={role} calendars={calendars} selected={selected} revision={events} />
          <TeamAvailability
            people={people}
            onChoose={(start, end, ids) => {
              if (!selected || current?.right_name === 'read') {
                setError(t('Choisissez un calendrier modifiable pour préparer la réunion.'));
                return;
              }
              setError('');
              setGuests(
                people
                  .filter((p) => ids.includes(p.id) && p.id !== self)
                  .map((p) => p.email)
                  .join(', '),
              );
              setGuestQuery('');
              setResourceId('');
              setAnchor(localDate(new Date(start)));
              setDraft({
                id: '',
                title: '',
                description: '',
                start: local(start),
                end: local(end),
                revision: 1,
              });
              focusEvent();
            }}
          />
        </>
      )}
    </section>
  );
}
export function ConversationWork({
  messageId,
  onOpenMessage,
}: {
  messageId: string;
  onOpenMessage: (message: { id: string; read: boolean }) => void;
}) {
  const [state, setState] = useState<any>(),
    [people, setPeople] = useState<Person[]>([]),
    [error, setError] = useState(''),
    [body, setBody] = useState(''),
    [busy, setBusy] = useState(false);
  const load = async () => {
    const result = await api(`/messages/${messageId}/collaboration`);
    setState(result);
    setPeople(result.assignees);
  };
  useEffect(() => {
    setState(undefined);
    setBody('');
    setError('');
    void load().catch((e) => {
      if (e.status !== 403) setError(e.message);
    });
  }, [messageId]);
  const run = async (fn: () => Promise<any>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  if (!state && !error) return null;
  return (
    <aside className="pro-card">
      <h3>{t('Suivi interne de la conversation')}</h3>
      {error && <Notice type="error">{error}</Notice>}
      {state && (
        <>
          <p>{t('Conversation : {count} message(s)', { count: state.messages.length })}</p>
          <details>
            <summary>{t('Messages de la conversation')}</summary>
            {state.messages.map((m: any) => (
              <p key={m.id}>
                <button type="button" onClick={() => onOpenMessage(m)}>
                  {new Date(m.received_at).toLocaleString()} : {m.subject}
                </button>
              </p>
            ))}
          </details>
          <label>
            {t('Attribué à')}
            <select
              disabled={busy}
              value={state.assigneeId ?? ''}
              onChange={(e) =>
                void run(() =>
                  send(
                    `/messages/${messageId}/collaboration`,
                    { assigneeId: e.target.value || null, revision: state.revision },
                    'PUT',
                  ),
                )
              }
            >
              <option value="">{t('Non attribué')}</option>
              {state.assigneeId && !people.some((p) => p.id === state.assigneeId) && (
                <option value={state.assigneeId} disabled>
                  {t('Attribution à revoir : accès révoqué')}
                </option>
              )}
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <p>
            {t(
              'Les notes restent dans la boîte partagée et ne sont pas envoyées aux destinataires.',
            )}
          </p>
          {state.notes.map((n: any) => (
            <article className="pro-event" key={n.id}>
              <strong>{n.author_name}</strong>
              <p style={{ whiteSpace: 'pre-wrap' }}>{n.body}</p>
            </article>
          ))}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await send(`/messages/${messageId}/notes`, { body });
                setBody('');
              });
            }}
          >
            <label>
              {t('Nouvelle note interne')}
              <textarea
                required
                maxLength={5000}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </label>
            <button disabled={busy}>{t('Ajouter la note')}</button>
          </form>
        </>
      )}
    </aside>
  );
}
